/**
 * useAnalytics — Hook unificado completo para rastreamento
 *
 * Baseado na arquitetura de factories do fimdatrilha.com.br.
 * Expõe todos os métodos de tracking com memoização.
 */

import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from './useI18n';
import { track, Events } from '@/lib/analytics';
import {
  trackWhatsAppClick,
  trackGenerateLead,
  trackPhoneCall,
  trackLeadMagnetDownload,
  trackCtaClick,
  trackBookingClick,
} from '@/lib/analytics/factories/contactFactory';
import {
  trackScroll,
  trackSectionView,
  trackLanguageChange,
  trackNavLinkClick,
  trackFooterLinkClick,
  trackMobileMenuToggle,
  trackAnnouncementClose,
  trackAppError,
} from '@/lib/analytics/factories/engagementFactory';

export function useAnalytics() {
  const location = useLocation();
  const { lang } = useI18n();
  const page = location.pathname;

  // ─── Legacy API (mantida para compatibilidade) ───
  const trackWhatsapp = useCallback(
    (p: { provider: string; source: string; entityType?: string; entityName?: string; entityId?: string; destination?: string }) => {
      track(Events.whatsappClick({
        provider: p.provider,
        source: p.source,
        entity_type: p.entityType,
        entity_name: p.entityName,
        entity_id: p.entityId,
        destination: p.destination,
        affiliate: false,
      }));
    },
    []
  );

  const trackBooking = useCallback(
    (p: { provider: string; entityType: string; entityName: string; entityId: string; price?: number; currency?: string }) => {
      track(Events.bookingClick({
        provider: p.provider,
        entity_type: p.entityType,
        entity_name: p.entityName,
        entity_id: p.entityId,
        price: p.price,
        currency: p.currency,
      }));
    },
    []
  );

  const trackAffiliate = useCallback(
    (p: { provider: string; platform: 'vai-de-promo' | 'booking' | 'outro'; productType: 'flight' | 'hotel' | 'insurance' | 'tour'; value?: number; currency?: string }) => {
      track(Events.affiliateClick({
        provider: p.provider,
        platform: p.platform,
        product_type: p.productType,
        value: p.value,
        currency: p.currency,
      }));
    },
    []
  );

  const trackNewsletter = useCallback(
    (source: string) => {
      track(Events.newsletterSignup(source, page, lang));
    },
    [page, lang]
  );

  const trackLeadMagnet = useCallback(
    (resource: string, source: string) => {
      track(Events.leadMagnetDownload(resource, source, lang));
    },
    [lang]
  );

  const trackEntityView = useCallback(
    (p: { entityType: string; entityName: string; entityId: string; category?: string; vilarejo?: string; partner?: boolean }) => {
      track(Events.entityView({
        entity_type: p.entityType,
        entity_name: p.entityName,
        entity_id: p.entityId,
        category: p.category,
        vilarejo: p.vilarejo,
        partner: p.partner,
      }));
    },
    []
  );

  const trackItinerary = useCallback(
    (id: string, name: string, days: number) => {
      track(Events.itineraryView(id, name, days, lang));
    },
    [lang]
  );

  const trackWidget = useCallback(
    (p: { widgetType: 'weather' | 'flights' | 'map' | 'wikiloc' | 'booking' | 'passes'; action: 'open' | 'search' | 'filter' | 'click' | 'close'; searchTerm?: string; resultCount?: number }) => {
      track(Events.widgetInteract({ widget_type: p.widgetType, action: p.action, search_term: p.searchTerm, result_count: p.resultCount }));
    },
    []
  );

  const trackOutbound = useCallback(
    (url: string, label: string, category: string) => {
      track(Events.outboundClick(url, label, category));
    },
    []
  );

  const trackShare = useCallback(
    (method: string, contentType: string, itemId: string) => {
      track(Events.share(method, contentType, itemId));
    },
    []
  );

  const trackVideo = useCallback(
    (title: string, provider: string, url: string) => {
      track(Events.videoStart(title, provider, url));
    },
    []
  );

  const trackGenerateLeadLegacy = useCallback(
    (value?: number, currency?: string) => {
      track(Events.generateLead(value, currency));
    },
    []
  );

  const trackSearch = useCallback(
    (term: string, category?: string, results?: number) => {
      track(Events.search(term, category, results));
    },
    []
  );

  // ─── Factory API (nova — baseada no fimdatrilha) ───
  const whatsapp = useCallback(
    (provider: string, source?: string) => {
      trackWhatsAppClick(provider, source);
    },
    []
  );

  const lead = useCallback(
    (formType: string, source?: string) => {
      trackGenerateLead(formType, source);
    },
    []
  );

  const phoneCall = useCallback(
    (phoneNumber: string, source?: string) => {
      trackPhoneCall(phoneNumber, source);
    },
    []
  );

  const leadMagnet = useCallback(
    (resource: string, source: string) => {
      trackLeadMagnetDownload(resource, source, lang);
    },
    [lang]
  );

  const cta = useCallback(
    (ctaText: string, ctaLocation: string, destination?: string) => {
      trackCtaClick(ctaText, ctaLocation, destination);
    },
    []
  );

  const booking = useCallback(
    (p: { provider: string; entityType: string; entityName: string; entityId: string; price?: number; currency?: string }) => {
      trackBookingClick(p.provider, p.entityType, p.entityName, p.entityId, p.price, p.currency);
    },
    []
  );

  const section = useCallback(
    (sectionName: string, sectionIndex: number) => {
      trackSectionView(sectionName, sectionIndex);
    },
    []
  );

  const nav = useCallback(
    (linkText: string, linkPath: string) => {
      trackNavLinkClick(linkText, linkPath);
    },
    []
  );

  const language = useCallback(
    (fromLocale: string, toLocale: string) => {
      trackLanguageChange(fromLocale, toLocale);
    },
    []
  );

  const menuToggle = useCallback(
    (action: 'open' | 'close') => {
      trackMobileMenuToggle(action);
    },
    []
  );

  const announceClose = useCallback(() => {
    trackAnnouncementClose();
  }, []);

  const appError = useCallback(
    (errorName: string, componentStack: string) => {
      trackAppError(errorName, componentStack, page);
    },
    [page]
  );

  return {
    // Legacy API
    track,
    trackWhatsapp,
    trackBooking,
    trackAffiliate,
    trackNewsletter,
    trackLeadMagnet,
    trackEntityView,
    trackItinerary,
    trackWidget,
    trackOutbound,
    trackShare,
    trackVideo,
    trackGenerateLead: trackGenerateLeadLegacy,
    trackSearch,

    // Factory API (nova)
    whatsapp,
    lead,
    phoneCall,
    leadMagnet,
    cta,
    booking,
    section,
    nav,
    language,
    menuToggle,
    announceClose,
    appError,
  };
}
